import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Controller } from "react-hook-form";
import AppContainer from "components/ui/AppContainer";
import AppInput from "components/ui/AppInput";
import AppButton from "components/ui/AppButton";
import AppSelect from "components/ui/AppSelect";
import AppTextArea from "components/ui/AppTextArea";

const PAYMENT_GATEWAYS = ["STRIPE", "PAYBY", "AFS_PAYMENT"];

export const AddPaymentLink = ({
  stores = [],
  onSubmit,
  control,
  errors,
  setValue,
  trigger,
  watch,
}) => {
  const { t } = useTranslation();

  const purposeLen = (watch("purposeOfPayment") || "").length;
  const messageLen = (watch("message") || "").length;

  const { integrations } = useSelector((state) => state.SettingsReducer);

  const paymentOptions = useMemo(() => {
    const opts = [];
    integrations?.forEach((integration) => {
      if (
        integration?.isActive &&
        PAYMENT_GATEWAYS.includes(integration?.name)
      ) {
        opts.push({
          label: integration?.name?.replaceAll("_", " "),
          value: integration?.name,
        });
      }
    });
    return opts;
  }, [integrations]);

  const setField = (key, value) => {
    setValue(key, value, { shouldValidate: true, shouldDirty: true });
    trigger(key);
  };

  useEffect(() => {
    if (paymentOptions?.length === 1) {
      setField("paymentGateway", paymentOptions[0].value);
    }
  }, [paymentOptions]);

  useEffect(() => {
    if (stores?.length === 1) {
      setField("storeSelection", String(stores[0].value));
    }
  }, [stores]);

  return (
    <AppContainer
      title={t("Create payment link")}
      subTitle={t(
        "Specify the amount to be paid and the purpose of payment to generate a link for your customers. Be as clear as possible, so they understand what they're paying for."
      )}
      showBackButton={true}
      backButtonText={t("Payment Links")}
      backButtonPath="/app/dashboard/payment-links"
      footer={
        <div className="flex items-center ">
          <AppButton
            type="submit"
            form="create-payment-link-form"
            variant="primary"
            size="lg"
            disabled={paymentOptions.length === 0}
          >
            {t("Create payment link")}
          </AppButton>
        </div>
      }
    >
      <form id="create-payment-link-form" onSubmit={onSubmit}>
        <div className="w-full pb-4">
          <div className="py-2">
            <div className="w-full sm:w-1/2">
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: t("Amount is a required field"),
                  validate: (v) => {
                    const n = Number(String(v ?? "").replace(/,/g, ""));
                    if (!Number.isFinite(n) || n <= 0) {
                      return t("Please enter a valid amount");
                    }
                    if (n < 2.1) {
                      return t("Amount should be greater than 2");
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <AppInput
                    label={t("Amount")}
                    type="number"
                    mandatory
                    placeholder="0.00"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    error={errors?.amount?.message}
                    inputProps={{ step: "0.01", min: "0" }}
                  >
                    <span
                      className="shrink-0 select-none text-sm font-semibold tabular-nums text-gray-500"
                      aria-hidden
                    >
                      AED
                    </span>
                  </AppInput>
                )}
              />
            </div>
          </div>
          {paymentOptions.length > 1 && 
            <div className="py-2 w-full sm:w-1/2">
            <Controller
              name="paymentGateway"
              control={control}
              rules={
                paymentOptions.length > 0
                  ? { required: t("Payment gateway is a required field") }
                  : {}
              }
              render={({ field }) => (
                <AppSelect
                  label={t("Select Payment Gateway")}
                  name="paymentGateway"
                  options={paymentOptions}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  placeholder={t("Select payment gateway")}
                  mandatory={paymentOptions.length > 0}
                  error={errors?.paymentGateway?.message}
                />
              )}
            />
          </div>}
          

          <div className="py-2 w-full sm:w-1/2">
            <Controller
              name="storeSelection"
              control={control}
              rules={{ required: t("Store is a required field") }}
              render={({ field }) => (
                <AppSelect
                  label={t("Select Store")}
                  name="storeSelection"
                  options={stores}
                  value={field.value}
                  onChange={(v) => field.onChange(v)}
                  placeholder={t("Select store")}
                  mandatory
                  error={errors?.storeSelection?.message}
                />
              )}
            />
          </div>

          <div className="py-2 w-full sm:w-1/2">
            <Controller
              name="purposeOfPayment"
              control={control}
              rules={{
                required: t("Purpose of payment is a required field"),
                minLength: {
                  value: 5,
                  message: t(
                    "Purpose of payment should be at least 5 characters long"
                  ),
                },
              }}
              render={({ field }) => (
                <AppTextArea
                  label={t("Purpose of payment")}
                  mandatory
                  rows={4}
                  placeholder={t("Enter purpose of payment")}
                  maxLength={300}
                  value={field.value}
                  onInput={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors?.purposeOfPayment?.message}
                />
              )}
            />
            <div className="mt-1 grid justify-items-end text-xs text-gray-500">
              {Math.max(0, 300 - purposeLen)} {t("characters remaining")}
            </div>
          </div>

          <div className="py-2 w-full sm:w-1/2">
            <Controller
              name="message"
              control={control}
              rules={{
                required: t("Message is a required field"),
                minLength: {
                  value: 5,
                  message: t("Message should be at least 5 characters long"),
                },
              }}
              render={({ field }) => (
                <AppTextArea
                  label={t("Message")}
                  mandatory
                  rows={3}
                  placeholder={t("Enter message")}
                  maxLength={300}
                  value={field.value}
                  onInput={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors?.message?.message}
                />
              )}
            />
            <div className="mt-1 grid justify-items-end text-xs text-gray-500">
              {Math.max(0, 300 - messageLen)} {t("characters remaining")}
            </div>
          </div>

          {paymentOptions.length === 0 ? (
            <p className="py-2 text-sm text-amber-700">
              {t("payment_link_no_gateways")}
            </p>
          ) : null}
        </div>
      </form>
    </AppContainer>
  );
};
